import React, { useEffect, useMemo } from 'react';

import type { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from 'next';
import Router from 'next/router';
import { TimePeriodAggEnum, TrendingArtistsSortEnum } from '@soundxyz/utils/const';
import { Collectors } from '@components/Collectors';
import { ArtistPick } from '@components/Profile/ArtistPick';
import { ArtistHero } from '@components/Profile/ProfileHero/ArtistHero';
import { AboutSection } from '@components/Profile/Sections/About';
import { SoundCollections } from '@components/Profile/Sections/SoundCollections';
import { UserInfo } from '@components/Profile/Sections/UserInfo';
import { ProfileContainer, ProfileWrapper } from '@components/Profile/shared';
import type { SeoProps } from '@components/Seo';
import { Seo } from '@components/Seo';
import { Spinner } from '@components/basics/Spinner';
import type { ArtistProfileNewQuery } from '@graphql/reactQuery';
import {
  ArtistProfileNewDocument,
  fetchGQL,
  getFragment,
  TrendingArtistInfoDocument,
  useQuery,
  UserSoundCollectionFragmentDoc,
} from '@graphql/reactQuery';
import { getUserOpenGraphImage } from '@utils/helpers';

const ISR_REVALIDATE_PERIOD = 20;

type PathParams = {
  soundHandle: string;
};

export const getStaticProps: GetStaticProps<PageProps, PathParams> = async ({ params }) => {
  const { soundHandle } = params || {};

  if (!soundHandle || Array.isArray(soundHandle)) {
    return {
      redirect: {
        destination: '/404',
        permanent: true,
      },
    };
  }

  return Promise.all([
    fetchGQL(ArtistProfileNewDocument, {
      variables: { soundHandle },
    }),
  ]).then(async ([data]) => {
    const artist = data.data.artistByHandle;
    const user = data.data.userByArtistHandle;
    if (!artist || !user) {
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
        revalidate: ISR_REVALIDATE_PERIOD,
      };
    }

    const userOg = getUserOpenGraphImage({ userId: user.id });
    /**
     * If there is no OG image and there is no banner photo then we show profile photo
     * If we show profile photo it needs to be small card
     */
    const smallTwitterCard = !userOg && !artist.bannerImage;

    const props: PageProps = {
      seo: {
        title: artist.name || null,
        twitterHandle: user.twitterHandle || null,
        description: user.description || null,
        image: userOg || user.avatar?.url || artist.bannerImage?.url || null,
        smallTwitterCard,
        audioUrl: null,
        url: artist.webappUri,
      },
      soundHandle,
      artist,
      user,
    };

    return {
      props,
      revalidate: ISR_REVALIDATE_PERIOD,
    };
  });
};

export const getStaticPaths: GetStaticPaths<PathParams> = async () => {
  return Promise.all([
    fetchGQL(TrendingArtistInfoDocument, {
      variables: {
        input: {
          sort: TrendingArtistsSortEnum.UNIQUE_COLLECTORS,
          timePeriod: TimePeriodAggEnum.SEVEN_DAY,
        },
      },
    }),
  ]).then(([data]) => {
    return {
      paths: data.data.topArtistsV2.reduce(
        (acc: GetStaticPathsResult<PathParams>['paths'], { artist }) => {
          if (artist?.soundHandle) {
            acc.push({
              params: {
                soundHandle: artist.soundHandle,
              },
            });
          }

          return acc;
        },
        [],
      ),
      fallback: true,
    };
  });
};

interface PageProps {
  seo: SeoProps;
  soundHandle: string;
  artist: NonNullable<ArtistProfileNewQuery['artistByHandle']>;
  user: NonNullable<ArtistProfileNewQuery['userByArtistHandle']>;
}

type SharedArtistProfilePropsType = {
  children?: React.ReactNode;
} & PageProps;

export default function ArtistProfile({
  soundHandle: soundHandleProp,
  seo,
  artist: artistProp,
  user: userProp,
}: Partial<SharedArtistProfilePropsType>) {
  // When using fallback, we don't have access to params through next router
  const soundHandle = useMemo(() => {
    if (soundHandleProp) return soundHandleProp;

    if (typeof window !== 'undefined') return new URL(window.location.href).pathname.split('/')[1];

    return null;
  }, [soundHandleProp]);

  const { data, isInitialLoading, isError, isRefetchError, isLoadingError, isLoading } = useQuery(
    ArtistProfileNewDocument,
    {
      variables: !!soundHandle && { soundHandle },
      initialData:
        artistProp && userProp
          ? () => ({
              data: {
                artistByHandle: artistProp,
                userByArtistHandle: userProp,
              },
            })
          : undefined,
      staleTime: 0,
    },
  );

  const user = data?.data.userByArtistHandle || userProp;
  const artistUserFragment = getFragment(UserSoundCollectionFragmentDoc, user);
  const artist = data?.data.artistByHandle || artistProp;

  const isArtist = !!artist && !!user?.artist;

  useEffect(() => {
    if (isArtist || isInitialLoading || isError || isRefetchError || isLoadingError) return;

    Router.replace('/404');
  }, [isArtist, isError, isInitialLoading, isLoadingError, isRefetchError]);

  // Only used on fallback, when cache can't be found
  if (!artist || !artistUserFragment || !user) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <Seo {...seo} />
      <ProfileContainer>
        <ArtistHero artistData={artist} />
        <ProfileWrapper>
          <UserInfo userData={user} loading={isLoading} />
          {(!!artist.pickedRelease || !!artist.numReleases) && <ArtistPick artistId={artist.id} />}
          <Collectors artist={artist} />
          <SoundCollections user={user} />
          <AboutSection aboutData={user} loading={isLoading} />
        </ProfileWrapper>
      </ProfileContainer>
    </>
  );
}
